import React, { useState, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { BiSearch, BiFilter, BiBookmark, BiUser, BiCog, BiCode, BiHelpCircle } from 'react-icons/bi';

const userGuideContent = `# User Guide

## Table of Contents
1. [Getting Started](#getting-started)
2. [Assessment Types](#assessment-types)
3. [Navigation](#navigation)
4. [Features](#features)
5. [Troubleshooting](#troubleshooting)
6. [FAQ](#faq)

## Getting Started

### First-Time Setup
1. Visit the application URL
2. Log in with your credentials
3. You will be directed to the History page
4. From there, you can access different assessment types

### Logging In
1. Click "Login" on the homepage
2. Enter your credentials
3. Click "Sign In"
4. You will be directed to the History page

## Assessment Types

### Readiness Assessment
The Readiness Assessment system evaluates your current performance across different dimensions:

1. **Dimensions**
   - Main categories of assessment
   - Each dimension contains specific indicators
   - Progress is tracked at the dimension level

2. **Indicators**
   - Specific measurable criteria within dimensions
   - Contains questions and evaluation points
   - Tracks completion status

3. **Questions**
   - Multiple question types:
     - Checkbox questions
     - Multi-statement questions
     - Input/calculation questions
     - Demographics questions
   - Required questions marked with asterisk (*)
   - Multi-language support (English, French, Malay)

### Progress Assessment
The Progress Assessment system tracks improvement over time:

1. **Progress Tracking**
   - Historical data comparison
   - Progress visualization
   - Improvement metrics
   - Trend analysis

2. **Assessment Levels**
   - Laggard (Red)
   - Beginner (Orange)
   - Intermediate (Light Green)
   - Advanced (Medium Green)
   - Leader (Dark Green)

## Navigation

### Main Menu
- History
- Assessment
- Documents
- Reports
- Profile

### Quick Actions
- Start new assessment
- Upload documents
- View reports
- Track progress

## Features

### Assessment Interface
1. **Question Display**
   - Clear question formatting
   - Required field indicators
   - Progress tracking
   - Auto-save functionality

2. **Document Management**
   - Upload supporting documents
   - View uploaded files
   - Document validation
   - File type restrictions

3. **Progress Tracking**
   - Real-time progress updates
   - Section completion status
   - Overall progress indicator
   - Auto-save recovery

### Multi-language Support
- English (default)
- French
- Malay
- Language switching
- RTL support

## Troubleshooting

### Common Issues
1. **Login Problems**
   - Clear browser cache
   - Check credentials
   - Contact support

2. **Assessment Issues**
   - Save progress regularly
   - Check internet connection
   - Validate required fields
   - Review error messages

3. **Document Upload**
   - Check file size limits
   - Verify file types
   - Ensure stable connection
   - Review upload status

### Error Messages
- "Session expired": Log in again
- "Invalid input": Check required fields
- "Upload failed": Retry document upload
- "Save failed": Check connection

## FAQ

### General Questions
1. **Q: How do I start a new assessment?**
   A: Go to the History page and click "Start New Assessment"

2. **Q: Can I save my progress?**
   A: Yes, the system auto-saves your progress regularly

3. **Q: How do I upload documents?**
   A: Click the "Upload Supporting Documents" button after completing questions

### Assessment Questions
1. **Q: What are the assessment levels?**
   A: Laggard (Red), Beginner (Orange), Intermediate (Light Green), Advanced (Medium Green), Leader (Dark Green)

2. **Q: Can I change my answers?**
   A: Yes, you can modify answers until final submission

3. **Q: How is progress tracked?**
   A: Progress is tracked by completed questions and sections

## Support

### Getting Help
- Email: tech@creativebox.world
- Phone: +27 11 771 41 42
- Live Chat: Available 24/7
- Help Center: https://work.creativebox.world/ticket-form/6920de42515209639c4fe778f1736f62?styled=1&with_logo=1

### Resources
- Video Tutorials
- Knowledge Base
- Community Forum
- Training Materials

## Tips and Best Practices

### Productivity Tips
1. Use keyboard shortcuts
2. Set up notifications
3. Organize your workspace
4. Use templates

### Security Best Practices
1. Enable 2FA
2. Use strong passwords
3. Regular security checks
4. Keep software updated

### Collaboration Tips
1. Use @mentions
2. Set clear deadlines
3. Regular updates
4. Document decisions

## Updates and Changes

### Recent Updates
- New dashboard layout
- Enhanced reporting tools
- Improved collaboration features
- Security enhancements

### Upcoming Features
- Mobile app
- Advanced analytics
`;

const regularAssessmentContent = `# Readiness Assessment Training Manual

## Table of Contents
1. [Introduction](#introduction)
2. [Getting Started with Readiness Assessment](#getting-started-with-readiness-assessment)
3. [Assessment Types](#assessment-types)
4. [Creating Assessments](#creating-assessments)
5. [Managing Assessments](#managing-assessments)
6. [Assessment Workflow](#assessment-workflow)
7. [Reporting and Analytics](#reporting-and-analytics)
8. [Best Practices](#best-practices)
9. [Troubleshooting](#troubleshooting)
10. [Resources](#resources)

## Introduction

### Purpose
This manual provides comprehensive guidance for conducting readiness assessments within the system. Readiness assessments are crucial for monitoring progress, identifying areas for improvement, and ensuring compliance with established standards. By implementing effective readiness assessment practices, organizations can:

- Maintain consistent quality standards
- Identify improvement opportunities
- Ensure regulatory compliance
- Track performance trends
- Make data-driven decisions
- Enhance organizational efficiency

### Target Audience
This manual is designed for various roles involved in assessment and quality management:

- **Assessment Coordinators**: Responsible for planning and executing assessments
- **Team Leaders**: Managing team performance and compliance
- **Department Managers**: Overseeing departmental assessments
- **Quality Assurance Personnel**: Ensuring quality standards
- **Compliance Officers**: Monitoring regulatory compliance

## Getting Started with Readiness Assessment

### Prerequisites
Before beginning readiness assessments, ensure you have:

1. **System Access and Permissions**
   - Appropriate user role and access level
   - Understanding of system navigation
   - Knowledge of basic system features
   - Required permissions for assessment creation and management

2. **Assessment Understanding**
   - Clear comprehension of assessment objectives
   - Knowledge of assessment criteria and standards
   - Understanding of assessment methodology
   - Familiarity with quality requirements

3. **Assessment Knowledge**
   - Understanding of assessment metrics and KPIs
   - Knowledge of assessment methodologies
   - Familiarity with reporting requirements
   - Understanding of data collection methods

4. **Timeline and Requirements**
   - Assessment schedule and deadlines
   - Key assessment criteria
   - Resource allocation plans
   - Quality requirements

### Accessing the Assessment Module
To begin using the Readiness Assessment module:

1. **System Login**
   - Navigate to the system login page
   - Enter your credentials
   - Complete any required authentication steps
   - Ensure you're using the correct environment (development/production)

2. **Navigation from History Page**
   - After login, you will be directed to the History page
   - From the History page, locate the navigation menu
   - Click on "Assessment" in the menu
   - This will take you to the Assessment section
   - Review available quick actions and features

3. **Module Selection**
   - Click on "Readiness Assessment" in the menu
   - Review the module overview
   - Understand the available features
   - Check your access permissions

## Assessment Types

The Readiness Assessment system is structured around dimensions and indicators:

### Dimensions
Dimensions are the main categories of assessment, each containing specific indicators. The system dynamically loads dimensions based on your access permissions and assessment requirements.

### Indicators
Each dimension contains multiple indicators that measure specific aspects of the assessment. Indicators include:
- Required text and descriptions
- Specific questions and criteria
- Progress tracking capabilities
- Completion status monitoring

### Assessment Structure
The assessment follows a hierarchical structure:
1. **Dimensions** (Main categories)
   - Each dimension represents a major area of assessment
   - Dimensions are loaded dynamically from the system
   - Progress is tracked at the dimension level

2. **Indicators** (Within each dimension)
   - Specific measurable criteria
   - Questions and evaluation points
   - Progress tracking and completion status
   - Required documentation

3. **Questions** (Within each indicator)
   - Required and optional questions (marked with asterisk)
   - Multiple question types:
     - Checkbox questions
     - Multi-statement questions
     - Input/calculation questions
     - Demographics questions
   - Multi-language support (English, French, Malay)
   - Document upload requirements
   - Validation rules

### Assessment Levels
The system tracks assessment levels using a standardized scale:
- Laggard (Red)
- Beginner (Orange)
- Intermediate (Light Green)
- Advanced (Medium Green)
- Leader (Dark Green)

Each level represents a different stage of maturity in the assessed areas.

## Creating Assessments

### Step-by-Step Guide

1. **Initiate New Assessment**
   - Click "Start New Assessment" button on the History page
   - The system will create a new assessment submission
   - You will be automatically redirected to the assessment interface
   - The submission ID will be stored in your session

2. **Assessment Interface**
   - Review the assessment sections and questions
   - Navigate through sections using the provided menu
   - Required questions are marked with an asterisk (*)
   - Answer questions within each section
   - Save your progress regularly (auto-save enabled)

3. **Document Upload**
   - Upload supporting documents as needed
   - Documents can be added to specific sections
   - Supported file types include PDF
   - Maximum file size limits apply

4. **Progress Tracking**
   - Monitor your completion status
   - Track time spent on each section
   - Review saved answers
   - Check for any incomplete sections
   - Progress is automatically saved to localStorage

## Managing Assessments

### Assessment Dashboard
The Assessment Dashboard provides a comprehensive view of all assessment activities:

1. **Overview Section**
   - Current assessment status
   - Assessment completion rates
   - Quality indicators
   - Compliance status
   - Performance metrics

2. **Assessment List**
   - Filterable view of all assessments
   - Priority-based sorting
   - Status tracking
   - Dependency management
   - Update monitoring

### Assessment Lifecycle
Understanding the complete lifecycle of assessments:

1. **Planning Phase**
   - Define assessment criteria
   - Set assessment objectives
   - Establish baselines
   - Plan assessment methods
   - Set up reporting requirements

2. **Execution Phase**
   - Conduct assessments
   - Collect assessment data
   - Document findings
   - Address issues
   - Validate results
`;

const progressAssessmentContent = `# Progress Assessment Training Manual

## Table of Contents
1. [Introduction](#introduction)
2. [Getting Started with Progress Assessment](#getting-started-with-progress-assessment)
3. [Progress Assessment Types](#progress-assessment-types)
4. [Creating Progress Assessments](#creating-progress-assessments)
5. [Managing Progress Assessments](#managing-progress-assessments)
6. [Progress Assessment Workflow](#progress-assessment-workflow)
7. [Progress Tracking and Analytics](#progress-tracking-and-analytics)
8. [Best Practices](#best-practices)
9. [Troubleshooting](#troubleshooting)
10. [Resources](#resources)

## Introduction

### Purpose
This manual provides comprehensive guidance for conducting progress assessments within the system. Progress assessments are essential for tracking project milestones, measuring development, and ensuring objectives are being met according to planned timelines. By implementing effective progress assessment practices, organizations can:

- Maintain project visibility and control
- Identify potential issues early
- Make data-driven decisions
- Ensure timely delivery of objectives
- Optimize resource allocation
- Improve team performance

### Target Audience
This manual is designed for various roles involved in project management and progress tracking:

- **Project Managers**: Responsible for overall project oversight and progress monitoring
- **Team Leaders**: Managing team performance and progress within their departments
- **Development Coordinators**: Tracking technical progress and development milestones
- **Stakeholders**: Interested in project status and progress updates
- **Quality Assurance Teams**: Monitoring quality-related progress and metrics

## Getting Started with Progress Assessment

### Prerequisites
Before beginning progress assessments, ensure you have:

1. **System Access and Permissions**
   - Appropriate user role and access level
   - Understanding of system navigation
   - Knowledge of basic system features
   - Required permissions for assessment creation and management

2. **Project Understanding**
   - Clear comprehension of project objectives
   - Knowledge of project scope and deliverables
   - Understanding of project timeline and milestones
   - Familiarity with team structure and responsibilities

3. **Progress Tracking Knowledge**
   - Understanding of progress metrics and KPIs
   - Knowledge of tracking methodologies
   - Familiarity with reporting requirements
   - Understanding of data collection methods

4. **Timeline and Deliverables**
   - Project schedule and deadlines
   - Key milestone dates
   - Resource allocation plans
   - Quality requirements

### Accessing the Progress Assessment Module
To begin using the Progress Assessment module:

1. **System Login**
   - Navigate to the system login page
   - Enter your credentials
   - Complete any required authentication steps
   - Ensure you're using the correct environment (development/production)

2. **Navigation from History Page**
   - After login, you will be directed to the History page
   - From the History page, locate the navigation menu
   - Click on "Assessment" in the menu
   - This will take you to the Assessment section
   - Review available quick actions and features

3. **Module Selection**
   - Click on "Progress Assessment" in the menu
   - Review the module overview
   - Understand the available features
   - Check your access permissions

## Progress Assessment Types

The Progress Assessment system is designed to track improvement over time, comparing current performance with previous assessments. The system is structured around dimensions and indicators:

### Dimensions
Progress dimensions are the main categories for tracking improvement, each containing specific indicators. The system dynamically loads dimensions based on your access permissions and assessment requirements.

### Indicators
Each dimension contains multiple indicators that measure specific aspects of progress. Indicators include:
- Required text and descriptions
- Specific questions and criteria
- Progress tracking capabilities
- Completion status monitoring
- Historical performance comparison

### Progress Assessment Structure
The assessment follows a hierarchical structure:
1. **Dimensions** (Main categories)
   - Each dimension represents a major area of progress tracking
   - Dimensions are loaded dynamically from the system
   - Progress is tracked at the dimension level
   - Historical data comparison is available

2. **Indicators** (Within each dimension)
   - Specific measurable criteria
   - Questions and evaluation points
   - Progress tracking and completion status
   - Required documentation
   - Improvement tracking

3. **Questions** (Within each indicator)
   - Required and optional questions (marked with asterisk)
   - Multiple question types:
     - Checkbox questions
     - Multi-statement questions
     - Input/calculation questions
     - Demographics questions
   - Multi-language support (English, French, Malay)
   - Document upload requirements
   - Validation rules
   - Progress comparison points

### Progress Levels
The system tracks progress levels using a standardized scale:
- Laggard (Red)
- Beginner (Orange)
- Intermediate (Light Green)
- Advanced (Medium Green)
- Leader (Dark Green)

Each level represents a different stage of progress in the assessed areas, with the ability to compare against previous assessments.

### Progress Tracking Features
The Progress Assessment includes specific features for tracking improvement:
- Historical data comparison
- Progress visualization
- Improvement metrics
- Trend analysis
- Performance tracking
- Auto-save functionality
- Progress recovery options

## Creating Progress Assessments

### Step-by-Step Guide

1. **Initiate New Progress Assessment**
   - Click "Start New Progress Assessment" button on the History page
   - The system will create a new progress assessment submission
   - You will be automatically redirected to the progress assessment interface
   - The submission ID will be stored in your session

2. **Progress Assessment Interface**
   - Review the progress assessment sections and questions
   - Navigate through sections using the provided menu
   - Required questions are marked with an asterisk (*)
   - Answer questions within each section
   - Save your progress regularly (auto-save enabled)
   - Compare with previous assessment data

3. **Document Upload**
   - Upload supporting documents as needed
   - Documents can be added to specific sections
   - Supported file types include PDF
   - Maximum file size limits apply

4. **Progress Tracking**
   - Monitor your completion status
   - Track time spent on each section
   - Review saved answers
   - Check for any incomplete sections
   - Progress is automatically saved to localStorage
   - Compare progress with previous assessments

## Managing Progress Assessments

### Progress Dashboard
The Progress Dashboard provides a comprehensive view of all assessment activities:

1. **Overview Section**
   - Current progress status with visual indicators
   - Milestone completion tracking
   - Risk indicators and alerts
   - Resource utilization metrics
   - Budget tracking information

2. **Progress Tracking List**
   - Filterable view of all tracking items
   - Priority-based sorting
   - Dependency tracking
   - Blocker identification
   - Status updates

### Progress Assessment Lifecycle
`;

const apiDocumentationContent = `# API Documentation

## Overview

The GCNSA Assessment System API provides endpoints for managing assessments, user authentication, and data retrieval. This documentation outlines the available endpoints, request/response formats, and authentication requirements.

## Base URL

~~~bash
Development: http://localhost:3000/api/v1
Production: https://api.example.com/api/v1
~~~

## Authentication

### Authentication Flow

1. **Login**
   ~~~http
   POST /auth/login
   Content-Type: application/json

   {
     "email": "user@example.com",
     "password": "password123"
   }
   ~~~

   Response:
   ~~~json
   {
     "token": "jwt_token",
     "user": {
       "id": "user_id",
       "email": "user@example.com",
       "name": "User Name",
       "role": "user"
     }
   }
   ~~~

2. **Token Usage**
   ~~~http
   Authorization: Bearer jwt_token
   ~~~

## Assessment Endpoints

### Readiness Assessment

1. **Start Assessment**
   ~~~http
   POST /assessments/regular/start
   Content-Type: application/json

   {
     "type": "regular",
     "dimensions": ["dimension_id1", "dimension_id2"]
   }
   ~~~

   Response:
   ~~~json
   {
     "id": "assessment_id",
     "status": "in_progress",
     "started_at": "2024-03-20T10:00:00Z"
   }
   ~~~

2. **Get Questions**
   ~~~http
   GET /assessments/regular/{assessment_id}/questions
   ~~~

   Response:
   ~~~json
   {
     "questions": [
       {
         "id": "question_id",
         "type": "checkbox",
         "text": "Question text",
         "required": true,
         "options": ["Option 1", "Option 2"]
       }
     ]
   }
   ~~~

3. **Submit Answer**
   ~~~http
   POST /assessments/regular/{assessment_id}/answers
   Content-Type: application/json

   {
     "question_id": "question_id",
     "answer": "selected_answer"
   }
   ~~~

4. **Upload Document**
   ~~~http
   POST /assessments/regular/{assessment_id}/documents
   Content-Type: multipart/form-data

   file: [document_file]
   ~~~

5. **Complete Assessment**
   ~~~http
   POST /assessments/regular/{assessment_id}/complete
   ~~~

### Progress Assessment

1. **Start Progress Assessment**
   ~~~http
   POST /assessments/progress/start
   Content-Type: application/json

   {
     "type": "progress",
     "previous_assessment_id": "previous_assessment_id"
   }
   ~~~

2. **Get Progress Questions**
   ~~~http
   GET /assessments/progress/{assessment_id}/questions
   ~~~

3. **Submit Progress Answer**
   ~~~http
   POST /assessments/progress/{assessment_id}/answers
   Content-Type: application/json

   {
     "question_id": "question_id",
     "answer": "selected_answer",
     "improvement": "improvement_description"
   }
   ~~~

4. **Complete Progress Assessment**
   ~~~http
   POST /assessments/progress/{assessment_id}/complete
   ~~~

## History Endpoints

1. **Get Assessment History**
   ~~~http
   GET /history
   Query Parameters:
   - type: regular|progress
   - status: completed|in_progress
   - page: number
   - limit: number
   ~~~

   Response:
   ~~~json
   {
     "assessments": [
       {
         "id": "assessment_id",
         "type": "regular",
         "status": "completed",
         "started_at": "2024-03-20T10:00:00Z",
         "completed_at": "2024-03-20T11:00:00Z"
       }
     ],
     "total": 100,
     "page": 1,
     "limit": 10
   }
   ~~~

2. **Get Assessment Details**
   ~~~http
   GET /history/{assessment_id}
   ~~~

## Report Endpoints

1. **Generate Report**
   ~~~http
   POST /reports/generate
   Content-Type: application/json

   {
     "assessment_id": "assessment_id",
     "type": "pdf|excel"
   }
   ~~~

2. **Get Report Status**
   ~~~http
   GET /reports/{report_id}/status
   ~~~
`;

const useGuideContent = `# User Guide

## Table of Contents
1. [Introduction](#introduction)
2. [Getting Started](#getting-started)
3. [Assessment Process](#assessment-process)
4. [Document Upload](#document-upload)
5. [Troubleshooting](#troubleshooting)

## Introduction

The SME and Supplier ESG Readiness Tool helps evaluate your company's Environmental, Social, and Governance (ESG) practices. This guide will walk you through the process of completing your assessment and uploading supporting documents.

## Getting Started

### Accessing the Assessment
1. Log in to your account
2. Navigate to the assessment page
3. You will be presented with a welcome screen containing:
   - Overview of the assessment
   - Estimated time required (30-45 minutes)
   - Information about the auto-save feature
   - Document upload requirements

### Starting the Assessment
1. Review the welcome information
2. Note the four key steps of the assessment process:
   - Answering all questions
   - Saving your progress
   - Navigating between sections
   - Uploading supporting documents
3. Click the "Start Assessment" button to begin with the first section

## Assessment Process

### Navigation
- Use the sidebar menu to navigate between different sections
- Each section is marked with an icon indicating its completion status:
  - ⚪ Not started
  - 🔵 In progress
  - ✅ Completed

### Answering Questions
1. Read each question carefully
2. Select or input your answer
3. Your progress is automatically saved
4. Use the "Save Section" button to manually save at any time

### Section Controls
- Use "Previous Section" and "Next Section" buttons to navigate
- The current section number is displayed (e.g., "Section 2 of 5")
- Each section must be completed before proceeding to document upload

### Auto-Save Feature
- Changes are automatically saved as you type
- A save indicator shows the status:
  - "Saving..." when changes are being saved
  - "All changes saved" when complete
  - "Error saving" if there's an issue

## Document Upload

### Requirements
- Document upload is only available after completing all assessment sections
- Supported file formats: PDF
- Each document must include:
  - Dimension selection
  - Document name
  - Description

### Upload Process
1. Click "Upload Documents" button (enabled after assessment completion)
2. Select the relevant dimension for each document
3. Add document name and description
4. Upload your PDF file
5. Add additional documents if needed
6. Submit when all documents are uploaded

### Document Types
Examples of acceptable supporting documents:
- Company policies or manuals
- ISO certifications
- Training certificates
- Business cards
- Company website screenshots

## Troubleshooting

### Common Issues
1. **Unable to proceed to next section**
   - Ensure all required questions are answered
   - Check for any error messages
   - Try saving the section manually

2. **Document upload button inactive**
   - Verify all sections are completed
   - Check if you're using a supported file format
   - Ensure all required fields are filled

3. **Auto-save not working**
   - Check your internet connection
   - Try manual save
   - Refresh the page if needed

### Support
If you encounter any issues:
- Use the help icon in the top navigation
- Contact support at support@smereadiness.com
- Check the FAQ section for common questions

## Tips for Success
1. Complete the assessment in one session if possible
2. Have supporting documents ready before starting
3. Use the auto-save feature but save manually at key points
4. Review your answers before final submission
5. Ensure documents are clear and relevant to each dimension 
`;

const KnowledgeBase = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedArticle, setSelectedArticle] = useState(null);

  // Define categories with their content
  const categories = useMemo(() => [
    {
      id: 'user-guides',
      name: 'User Guides',
      icon: <BiUser className="text-blue-500" />,
      articles: [
        {
          id: 'getting-started',
          title: 'Getting Started Guide',
          content: userGuideContent,
          tags: ['beginner', 'setup', 'navigation']
        },
        {
          id: 'assessment-process',
          title: 'Assessment Process Guide',
          content: useGuideContent,
          tags: ['assessment', 'process', 'workflow']
        }
      ]
    },
    {
      id: 'assessment-manuals',
      name: 'Assessment Manuals',
      icon: <BiBookmark className="text-green-500" />,
      articles: [
        {
          id: 'readiness-assessment',
          title: 'Readiness Assessment Manual',
          content: regularAssessmentContent,
          tags: ['readiness', 'assessment', 'manual', 'training']
        },
        {
          id: 'progress-assessment',
          title: 'Progress Assessment Manual',
          content: progressAssessmentContent,
          tags: ['progress', 'assessment', 'manual', 'tracking']
        }
      ]
    },
    {
      id: 'technical-docs',
      name: 'Technical Documentation',
      icon: <BiCode className="text-purple-500" />,
      articles: [
        {
          id: 'api-documentation',
          title: 'API Documentation',
          content: apiDocumentationContent,
          tags: ['api', 'technical', 'integration', 'developers']
        }
      ]
    },
    {
      id: 'troubleshooting',
      name: 'Troubleshooting',
      icon: <BiHelpCircle className="text-red-500" />,
      articles: [
        {
          id: 'common-issues',
          title: 'Common Issues & Solutions',
          content: `# Common Issues & Solutions

## Login Problems
- **Issue**: Unable to log in
- **Solution**: Clear browser cache and check credentials
- **Contact**: tech@creativebox.world

## Assessment Issues
- **Issue**: Assessment not saving
- **Solution**: Check internet connection and use auto-save feature
- **Prevention**: Save progress regularly

## Document Upload Issues
- **Issue**: Files not uploading
- **Solution**: Check file size limits and supported formats (PDF only)
- **Alternative**: Try smaller files or different browser

## System Errors
- **Issue**: "Session expired" message
- **Solution**: Log in again and refresh the page
- **Prevention**: Don't leave the page idle for extended periods

## Navigation Issues
- **Issue**: Can't proceed to next section
- **Solution**: Ensure all required questions are answered
- **Check**: Look for asterisk (*) marked required fields

## Support Contact
- Email: tech@creativebox.world
- Phone: +27 11 771 41 42
- Live Chat: Available 24/7`,
          tags: ['troubleshooting', 'support', 'issues', 'help']
        }
      ]
    }
  ], []);

  // Filter articles based on search and category
  const filteredArticles = useMemo(() => {
    let articles = [];
    
    categories.forEach(category => {
      if (selectedCategory === 'all' || category.id === selectedCategory) {
        articles.push(...category.articles);
      }
    });

    if (searchTerm) {
      articles = articles.filter(article => 
        article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        article.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    return articles;
  }, [categories, selectedCategory, searchTerm]);

  // Get all unique tags
  const allTags = useMemo(() => {
    const tags = new Set();
    categories.forEach(category => {
      category.articles.forEach(article => {
        article.tags.forEach(tag => tags.add(tag));
      });
    });
    return Array.from(tags).sort();
  }, [categories]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Knowledge Base</h1>
              <p className="mt-2 text-gray-600">
                Comprehensive guides and documentation for the ESG Assessment Platform
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <div className="relative">
                <BiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search articles..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm border p-6 sticky top-8">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <BiFilter className="mr-2" />
                Categories
              </h2>
              
              {/* Category Filter */}
              <div className="space-y-2 mb-6">
                <button
                  onClick={() => setSelectedCategory('all')}
                  className={`w-full text-left px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    selectedCategory === 'all'
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  All Categories
                </button>
                {categories.map(category => (
                  <button
                    key={category.id}
                    onClick={() => setSelectedCategory(category.id)}
                    className={`w-full text-left px-3 py-2 rounded-md text-sm font-medium transition-colors flex items-center ${
                      selectedCategory === category.id
                        ? 'bg-blue-100 text-blue-700'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    {category.icon}
                    <span className="ml-2">{category.name}</span>
                  </button>
                ))}
              </div>

              {/* Popular Tags */}
              <div>
                <h3 className="text-sm font-medium text-gray-900 mb-3">Popular Tags</h3>
                <div className="flex flex-wrap gap-2">
                  {allTags.slice(0, 10).map(tag => (
                    <button
                      key={tag}
                      onClick={() => setSearchTerm(tag)}
                      className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-md hover:bg-gray-200 transition-colors"
                    >
                      {tag}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            {selectedArticle ? (
              /* Article View */
              <div className="bg-white rounded-lg shadow-sm border">
                <div className="p-6 border-b">
                  <button
                    onClick={() => setSelectedArticle(null)}
                    className="text-blue-600 hover:text-blue-800 mb-4 flex items-center"
                  >
                    ← Back to Articles
                  </button>
                  <h1 className="text-2xl font-bold text-gray-900">{selectedArticle.title}</h1>
                  <div className="flex items-center mt-2 space-x-4">
                    <div className="flex items-center space-x-2">
                      {selectedArticle.tags.map(tag => (
                        <span
                          key={tag}
                          className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-md"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="p-6 prose max-w-none">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {selectedArticle.content}
                  </ReactMarkdown>
                </div>
              </div>
            ) : (
              /* Articles List */
              <div>
                <div className="mb-6">
                  <h2 className="text-xl font-semibold text-gray-900 mb-2">
                    {searchTerm ? `Search Results for "${searchTerm}"` : 'All Articles'}
                  </h2>
                  <p className="text-gray-600">
                    {filteredArticles.length} article{filteredArticles.length !== 1 ? 's' : ''} found
                  </p>
                </div>

                <div className="grid gap-6">
                  {filteredArticles.map(article => (
                    <div
                      key={article.id}
                      className="bg-white rounded-lg shadow-sm border p-6 hover:shadow-md transition-shadow cursor-pointer"
                      onClick={() => setSelectedArticle(article)}
                    >
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        {article.title}
                      </h3>
                      <div className="flex items-center space-x-2 mb-3">
                        {article.tags.map(tag => (
                          <span
                            key={tag}
                            className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-md"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                      <p className="text-gray-600 text-sm">
                        Click to read the full article...
                      </p>
                    </div>
                  ))}
                </div>

                {filteredArticles.length === 0 && (
                  <div className="text-center py-12">
                    <BiSearch className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900">No articles found</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Try adjusting your search terms or browse all categories.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default KnowledgeBase;
